  import React, { useEffect, useState } from 'react';
  import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
    Stack,
    Tooltip,
    useTheme,
    useMediaQuery,
  } from '@mui/material';
  import {
    Shield as ShieldIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Speed as SpeedIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
  } from '@mui/icons-material';
  import { useNotifications } from '../contexts/NotificationContext';
  import { formatDistanceToNow } from 'date-fns';
  import axios from 'axios';

  interface GeofenceViolation {
    _id: string;
    vehicleId: string;
    plateNumber: string;
    make: string;
    model: string;
    timestamp: string;
    zoneName: string;
    zoneId: string;
    violationType: 'unauthorized_entry' | 'unauthorized_exit' | 'breach' | 'speed_violation';
    location: {
      coordinates: [number, number];
    };
    speed?: number;
    currentSpeed?: number;
    speedLimit?: number;
    acknowledgedBy?: string;
    acknowledgedAt?: string;
  }

  const GeofenceAlertsWidget: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [violations, setViolations] = useState<GeofenceViolation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { socket } = useNotifications();

    const fetchViolations = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:1001/api'}/geofencing/violations`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              acknowledged: 'false',
              limit: 10,
            }
          }
        );

        if (response.data.success) {
          setViolations(response.data.data);
        }
        setError(null);
      } catch (err: any) {
        console.error('Error fetching violations:', err);
        setError('Failed to load violations');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchViolations();

      if (socket) {
        socket.on('geofence_notification', (data: any) => {
          if (data.metadata?.violationType) {
            fetchViolations();
          }
        });
      }

      const interval = setInterval(fetchViolations, 30000);

      return () => {
        clearInterval(interval);
        if (socket) {
          socket.off('geofence_notification');
        }
      };
    }, [socket]);

    const getViolationIcon = (type: string) => {
      const iconSize = isMobile ? 16 : 20;
      switch (type) {
        case 'unauthorized_entry':
        case 'unauthorized_exit':
          return <ErrorIcon sx={{ color: '#d32f2f', fontSize: iconSize }} />;
        case 'speed_violation':
          return <SpeedIcon sx={{ color: '#ed6c02', fontSize: iconSize }} />;
        case 'breach':
          return <WarningIcon sx={{ color: '#f57c00', fontSize: iconSize }} />;
        default:
          return <ShieldIcon sx={{ color: '#1976d2', fontSize: iconSize }} />;
      }
    };

    const getViolationLabel = (type: string) => {
      const labels: { [key: string]: string } = {
        unauthorized_entry: isMobile ? 'Entry' : 'Unauthorized Entry',
        unauthorized_exit: isMobile ? 'Exit' : 'Unauthorized Exit',
        speed_violation: isMobile ? 'Speed' : 'Speed Violation',
        breach: isMobile ? 'Breach' : 'Zone Breach',
      };
      return labels[type] || type;
    };

    const getViolationColor = (type: string) => {
      switch (type) {
        case 'unauthorized_entry':
        case 'unauthorized_exit':
          return 'error';
        case 'speed_violation':
          return 'warning';
        case 'breach':
          return 'warning';
        default:
          return 'info';
      }
    };

    const acknowledgeViolation = async (vehicleId: string, violationId: string) => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${process.env.REACT_APP_API_URL || 'http://localhost:1001/api'}/geofencing/vehicles/${vehicleId}/violations/${violationId}/acknowledge`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        fetchViolations();
      } catch (err) {
        console.error('Error acknowledging violation:', err);
      }
    };

    if (loading && violations.length === 0) {
      return (
        <Card>
          <CardHeader 
            title={
              <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                Recent Geofence Violations
              </Typography>
            }
            avatar={<ShieldIcon sx={{ fontSize: isMobile ? 20 : 24 }} />}
          />
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={isMobile ? 24 : 40} />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader
          avatar={<ShieldIcon color={violations.length > 0 ? 'error' : 'primary'} sx={{ fontSize: isMobile ? 20 : 24 }} />}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
                {isMobile ? 'Violations' : 'Recent Geofence Violations'}
              </Typography>
              {violations.length > 0 && (
                <Chip 
                  label={violations.length} 
                  color="error" 
                  size="small"
                  sx={{ 
                    animation: 'pulse 2s ease-in-out infinite',
                    height: isMobile ? 20 : 24,
                    fontSize: isMobile ? '0.7rem' : '0.8125rem'
                  }}
                />
              )}
            </Box>
          }
          action={
            <Tooltip title="Refresh">
              <IconButton onClick={fetchViolations} disabled={loading} size={isMobile ? 'small' : 'medium'}>
                <RefreshIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
          }
          sx={{
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.6 },
            },
            p: isMobile ? 1.5 : 2,
          }}
        />
        <CardContent sx={{ p: isMobile ? 1.5 : 2, pt: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {violations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
              <CheckCircleIcon sx={{ fontSize: isMobile ? 32 : 48, color: 'success.main', mb: 1 }} />
              <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
                No active violations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All vehicles are within authorized zones
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: isMobile ? 300 : 400, overflow: 'auto', p: 0 }}>
              {violations.map((violation, index) => (
                <React.Fragment key={violation._id}>
                  <ListItem
                    sx={{
                      bgcolor: 'error.lighter',
                      borderRadius: 1,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'error.light',
                      p: isMobile ? 1 : 1.5,
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      <ListItemIcon sx={{ minWidth: isMobile ? 32 : 40 }}>
                        {getViolationIcon(violation.violationType)}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Stack spacing={0.5}>
                            <Typography variant={isMobile ? 'caption' : 'subtitle2'} fontWeight="bold">
                              {violation.plateNumber} - {violation.make} {violation.model}
                            </Typography>
                            <Chip
                              size="small"
                              label={getViolationLabel(violation.violationType)}
                              color={getViolationColor(violation.violationType) as any}
                              sx={{ 
                                height: isMobile ? 18 : 20, 
                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                width: 'fit-content'
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Zone: <strong>{violation.zoneName}</strong>
                            </Typography>
                            
                            {violation.violationType === 'speed_violation' && violation.currentSpeed && (
                              <Typography variant="caption" color="error" fontWeight="bold" display="block">
                                Speed: {Math.round(violation.currentSpeed)} km/h
                                {violation.speedLimit && ` (Limit: ${violation.speedLimit} km/h)`}
                              </Typography>
                            )}

                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {formatDistanceToNow(new Date(violation.timestamp), { addSuffix: true })}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>

                    <Tooltip title="View & Acknowledge">
                      <IconButton
                        size="small"
                        onClick={() => acknowledgeViolation(violation.vehicleId, violation._id)}
                        sx={{ 
                          mt: isMobile ? 1 : 0,
                          ml: isMobile ? 'auto' : 1,
                          alignSelf: isMobile ? 'flex-end' : 'center'
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>

                  {index < violations.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </React.Fragment>
              ))}
            </List>
          )}

          {violations.length > 0 && (
            <Box sx={{ textAlign: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                {isMobile ? 'Tap to acknowledge' : 'Click on a violation to acknowledge and view details'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  export default GeofenceAlertsWidget;